/**
 * <wdka-carousel> — the public-facing slide display.
 *
 * Replaces the old Vue carousel. Built for week-long uptime on
 * unattended screens, so the cardinal rule is: media elements are
 * created ONCE and reused forever. Slides are rendered into two
 * persistent layers (A/B) that crossfade; changing slides re-points
 * the existing <video>/<img> elements at new sources instead of
 * destroying and recreating them. Chromium leaks detached media
 * elements even after a spec-correct release, so the only robust
 * strategy is to never detach them at all.
 *
 * Performance: the next slide is prepared in the hidden layer DURING
 * the current slide's dwell, and its image/video is decoded before the
 * crossfade starts. On weak screen hardware (4K portrait panels on ARM
 * SoCs) this removes the staggered "pop-in" of images at swap time.
 *
 * Structure follows Chris Ferdinandi's web component conventions:
 * light DOM, an init() separate from connectedCallback(), the
 * handleEvent() listener pattern, private (#) members for internals,
 * an [is-ready] guard/state hook, and namespaced custom events.
 * https://gomakethings.com/guides/web-components/
 */

import { Playlist } from "./playlist";
import "./clock"; // registers the self-contained <wdka-clock> element
import { PerfLogger } from "./perf-logger";

const FADE_MS = 786; // matches the old Vue fade transition
const RING_RADIUS = 60;
const RING_STROKE = 24;
const RING_CIRCUMFERENCE = 2 * Math.PI * (RING_RADIUS - RING_STROKE * 2);
const VIDEO_GRACE_S = 5; // backstop after the video should have ended
const PRELOAD_TIMEOUT_MS = 8000; // never let a stalled asset block the show
const RELOAD_HOUR = 4; // nightly watchdog reload, 04:00 local
const MEDIA_LOG_EVENTS = [
  "loadstart",
  "loadedmetadata",
  "loadeddata",
  "canplay",
  "play",
  "playing",
  "waiting",
  "stalled",
  "suspend",
  "error",
  "ended"
];

const LAYER_TEMPLATE = `
  <section class="slide__video" hidden>
    <video muted playsinline preload="auto"></video>
  </section>
  <section class="slide__top" hidden>
    <svg class="progress-ring" height="${RING_RADIUS * 2}" width="${RING_RADIUS * 2}">
      <circle
        class="progress"
        fill="transparent"
        r="${RING_RADIUS - RING_STROKE * 2}"
        cx="${RING_RADIUS}"
        cy="${RING_RADIUS}"
        stroke-dasharray="${RING_CIRCUMFERENCE} ${RING_CIRCUMFERENCE}"
        style="stroke-width: ${RING_STROKE}px"
      />
    </svg>
    <h2 class="slide__title"></h2>
    <div class="slide__content"></div>
  </section>
  <img class="slide__image" alt="" decoding="async" hidden />
  <div class="video__bg" hidden></div>
  <video class="slide__background-video" muted loop playsinline preload="auto" hidden></video>
`;

/** Release a video element's current resource without detaching it. */
function releaseVideo(video) {
  try {
    video.pause();
    video.removeAttribute("src");
    video.load();
  } catch (e) {
    /* nothing to release */
  }
}

function isPromiseLike(value) {
  return value && typeof value.then === "function";
}

class WdkaCarousel extends HTMLElement {
  #playlist;
  #layers = [];
  #activeIndex = 0;
  #pending = null; // { slide, layer, ready: Promise } — preloaded next
  #advanceTimer = null;
  #reloadTimer = null;
  #perf = null;

  connectedCallback() {
    // Guard against re-running setup if the element is moved in the DOM.
    if (this.hasAttribute("is-ready")) return;
    this.init();
  }

  disconnectedCallback() {
    clearTimeout(this.#advanceTimer);
    clearTimeout(this.#reloadTimer);
    document.removeEventListener("keyup", this);
    if (this.#perf) this.#perf.stop();
  }

  init() {
    const bootstrap = window.app?.slides ?? [];
    const feeds = window.central?.central_feeds ?? [];
    this.#perf = new PerfLogger(window.wdkaCarouselDiagnostics);
    this.#perf.start();
    this.#playlist = new Playlist(bootstrap, feeds);
    this.#perf.record("playlist-init", {
      slideCount: this.#playlist.length,
      feedCount: feeds.length
    });

    this.#render();

    document.addEventListener("keyup", this);

    this.#scheduleNightlyReload();

    this.setAttribute("is-ready", "");
    this.dispatchEvent(
      new CustomEvent("wdka-carousel:ready", { bubbles: true })
    );

    this.#start();
  }

  /* ---- Public API: developers/operators may drive it manually ---- */

  next() {
    this.#goNext();
  }

  previous() {
    this.#goPrev();
  }

  /* ---- Single event entry point (handleEvent pattern) ---- */

  handleEvent(event) {
    if (event.type === "keyup") {
      this.#handleKeyup(event);
      return;
    }
    this.#handleMediaEvent(event);
  }

  /* ---- Private ---- */

  #render() {
    this.innerHTML = `<div class="carousel"></div>`;
    const container = this.firstElementChild;
    const diagnostics = window.wdkaCarouselDiagnostics || {};
    const diagnosticsVersion = diagnostics.enabled && diagnostics.version
      ? `<div class="diagnostics-version">build ${diagnostics.version}</div>`
      : "";

    this.#layers = [0, 1].map(() => {
      const el = document.createElement("div");
      el.className = "slide layer";
      el.innerHTML = LAYER_TEMPLATE;
      container.appendChild(el);

      const layer = {
        el,
        slide: null,
        videoSection: el.querySelector(".slide__video"),
        video: el.querySelector(".slide__video video"),
        top: el.querySelector(".slide__top"),
        ring: el.querySelector(".progress-ring .progress"),
        title: el.querySelector(".slide__title"),
        content: el.querySelector(".slide__content"),
        img: el.querySelector(".slide__image"),
        videoBg: el.querySelector(".video__bg"),
        bgVideo: el.querySelector(".slide__background-video")
      };

      // Persistent listeners drive the ACTIVE slide video's lifecycle;
      // they live for the element's lifetime (routed via #handleMediaEvent).
      MEDIA_LOG_EVENTS.forEach(type => {
        layer.video.addEventListener(type, this);
        layer.bgVideo.addEventListener(type, this);
      });

      return layer;
    });

    container.insertAdjacentHTML("beforeend", "<wdka-clock></wdka-clock>");
    container.insertAdjacentHTML(
      "beforeend",
      `<section class="overlay"><div class="message">wdka.tv@hr.nl</div>${diagnosticsVersion}</section>`
    );
  }

  async #start() {
    if (!this.#playlist.length) return; // nothing to show (own bootstrap empty)
    const layer = this.#layers[0];
    const slide = this.#playlist.current;
    await this.#prepare(layer, slide);
    this.#activate(layer, slide);
    this.#preloadNext();
  }

  /** Populate a layer and resolve once its media is decoded/ready. */
  #prepare(layer, slide) {
    const started = performance.now();
    this.#populate(layer, slide);
    return this.#whenReady(layer, slide).then(reason => {
      this.#perf.recordPrepare(slide, layer, performance.now() - started, reason);
      return reason;
    });
  }

  #whenReady(layer, slide) {
    return new Promise(resolve => {
      let done = false;
      const finish = reason => {
        if (done) return;
        done = true;
        clearTimeout(timer);
        resolve(reason || "ready");
      };
      // Hard cap: a slow/broken asset must never stall the rotation.
      const timer = setTimeout(() => finish("timeout"), PRELOAD_TIMEOUT_MS);

      if (slide.video) {
        const v = layer.video;
        if (v.readyState >= 2) return finish("already-ready"); // HAVE_CURRENT_DATA
        v.addEventListener("loadeddata", () => finish("loadeddata"), { once: true });
        v.addEventListener("error", () => finish("error"), { once: true });
      } else if (slide.img && !slide.backgroundVideo && layer.img.decode) {
        // Decode the still image off the critical path, before it's shown.
        layer.img.decode().then(
          () => finish("decoded"),
          () => finish("decode-error")
        );
      } else {
        finish("no-media");
      }
    });
  }

  /**
   * Prepare the upcoming slide in the hidden layer during the dwell — but
   * ONLY for non-video slides. Decoding a second video while one plays
   * exceeds the i.MX6 VPU's single-stream capacity and forces software
   * decode; video slides are prepared at transition time instead.
   */
  #preloadNext() {
    const slide = this.#playlist.peekNext();
    if (!slide || slide.video || slide.backgroundVideo) {
      this.#pending = null;
      return;
    }
    const layer = this.#layers[1 - this.#activeIndex];
    this.#pending = { slide, layer, ready: this.#prepare(layer, slide) };
  }

  #goNext() {
    this.#transitionTo(this.#playlist.advance());
  }

  #goPrev() {
    this.#transitionTo(this.#playlist.rewind());
  }

  /**
   * EXPERIMENT (hardware video decode): instant cut instead of an opacity
   * crossfade, and never more than one <video> decoding at a time.
   * Embedded Chromium only hardware-decodes a plain, opaque, single video
   * overlay — a fading layer or a second decoding stream forces it into
   * software decode on the weak CPU.
   */
  #transitionTo(slide) {
    if (!slide) return;
    const outgoing = this.#layers[this.#activeIndex];
    const incoming = this.#layers[1 - this.#activeIndex];

    // Stop the outgoing video now, before any new video starts decoding.
    if (outgoing.slide && outgoing.slide.video) releaseVideo(outgoing.video);
    if (outgoing.slide && outgoing.slide.backgroundVideo) {
      releaseVideo(outgoing.bgVideo);
    }

    const pending = this.#pending;
    this.#pending = null;

    if (pending && pending.slide === slide && pending.layer === incoming) {
      this.#cut(incoming, slide);
    } else {
      this.#prepare(incoming, slide).then(() => this.#cut(incoming, slide));
    }
  }

  #cut(layer, slide) {
    this.#activate(layer, slide);
    this.#perf.record("transition", {
      completed: true,
      hardCut: true,
      slide: slide.title || ""
    });
    this.#preloadNext();
  }

  #activate(layer, slide) {
    this.#activeIndex = this.#layers.indexOf(layer);
    layer.el.classList.add("is-active");
    this.#layers[1 - this.#activeIndex].el.classList.remove("is-active");

    // A foreground video slide is presented as a clean, opaque, single
    // plane: every overlay (clock, progress ring, branding, title/content)
    // is hidden so nothing is composited on top of the <video>, giving
    // embedded Chromium its best shot at the hardware video-overlay path.
    // Background-video slides are excluded — they are video-behind-text by
    // design and keep their overlays. Image/text slides keep them too.
    this.classList.toggle("is-video-active", Boolean(slide.video));

    if (slide.backgroundVideo) {
      try {
        const result = layer.bgVideo.play();
        if (isPromiseLike(result)) {
          result.then(
            () => this.#perf.recordPlayResult("background", slide, layer.bgVideo),
            error => this.#perf.recordPlayResult("background", slide, layer.bgVideo, error)
          );
        } else {
          this.#perf.recordPlayResult("background", slide, layer.bgVideo);
        }
      } catch (error) {
        this.#perf.recordPlayResult("background", slide, layer.bgVideo, error);
      }
    }

    if (slide.video) {
      try {
        const result = layer.video.play();
        if (isPromiseLike(result)) {
          result.then(
            () => this.#perf.recordPlayResult("slide", slide, layer.video),
            error => this.#perf.recordPlayResult("slide", slide, layer.video, error)
          );
        } else {
          this.#perf.recordPlayResult("slide", slide, layer.video);
        }
      } catch (error) {
        this.#perf.recordPlayResult("slide", slide, layer.video, error);
      }
      const duration = layer.video.duration || slide.duration || 10;
      this.#schedule(duration + VIDEO_GRACE_S);
    } else {
      this.#startRing(layer, slide.duration);
      this.#schedule(slide.duration);
    }

    this.dispatchEvent(
      new CustomEvent("wdka-carousel:change", {
        bubbles: true,
        detail: { slide, index: this.#playlist.index }
      })
    );
    this.#perf.recordSlideChange(slide, this.#playlist.index, layer);
  }

  /**
   * Only the active layer's slide video drives advancement; the buffer
   * layer's media events (it's loading, not playing) are ignored.
   */
  #handleMediaEvent(event) {
    const layer = this.#layers[this.#activeIndex];
    const eventLayer = this.#layers.find(item => {
      return event.target === item.video || event.target === item.bgVideo;
    });
    if (eventLayer) {
      this.#perf.recordMediaEvent(
        event,
        event.target === eventLayer.bgVideo ? "background" : "slide",
        eventLayer
      );
    }

    if (event.target !== layer.video || !layer.slide || !layer.slide.video) {
      return;
    }
    if (event.type === "ended") {
      this.#goNext();
    } else if (event.type === "error") {
      this.#schedule(layer.slide.duration || 10); // skip a broken video
    }
  }

  #handleKeyup({ key }) {
    switch (key) {
      case "ArrowRight":
        this.#goNext();
        break;
      case "ArrowLeft":
        this.#goPrev();
        break;
      case " ":
        clearTimeout(this.#advanceTimer);
        break;
    }
  }

  /** Set a layer's sources/markup. Does NOT start playback (see #activate). */
  #populate(layer, slide) {
    const options = Array.isArray(slide.options)
      ? slide.options
      : slide.options
        ? [slide.options]
        : [];

    layer.slide = slide;
    // Only the hidden/buffer layer is ever populated, so this className
    // reset never strips is-active from a visible layer; #activate alone
    // controls visibility.
    layer.el.className = ["slide", "layer", `theme--${slide.theme}`]
      .concat(options)
      .join(" ");

    layer.title.textContent = slide.title;
    layer.content.innerHTML = slide.content;

    // Slide video — set source and kick the load; playback waits for activate.
    if (slide.video) {
      layer.videoSection.hidden = false;
      layer.top.hidden = true;
      layer.video.src = slide.video.url;
      layer.video.load();
    } else {
      layer.videoSection.hidden = true;
      layer.top.hidden = false;
      releaseVideo(layer.video);
    }

    // Background video
    if (slide.backgroundVideo) {
      layer.videoBg.hidden = false;
      layer.videoBg.className = `video__bg theme--${slide.theme} filter--${slide.blendMode}`;
      layer.bgVideo.hidden = false;
      layer.bgVideo.src = slide.backgroundVideo.url;
      layer.bgVideo.load();
    } else {
      layer.videoBg.hidden = true;
      layer.bgVideo.hidden = true;
      releaseVideo(layer.bgVideo);
    }

    // Still image (only when no video media is shown, as before)
    if (slide.img && !slide.video && !slide.backgroundVideo) {
      layer.img.hidden = false;
      layer.img.className = `slide__image filter--${slide.blendMode}`;
      layer.img.src = slide.img.url;
      layer.img.alt = slide.img.alt;
    } else {
      layer.img.hidden = true;
      layer.img.removeAttribute("src");
    }
  }

  #startRing(layer, seconds) {
    const ring = layer.ring;
    ring.style.transition = "none";
    ring.style.strokeDashoffset = 0;
    void ring.getBoundingClientRect(); // flush, so the transition restarts
    ring.style.transition = `stroke-dashoffset ${seconds}s linear`;
    ring.style.strokeDashoffset = RING_CIRCUMFERENCE;
  }

  /** The single advance timer; every (re)schedule clears the previous one. */
  #schedule(seconds) {
    clearTimeout(this.#advanceTimer);
    this.#advanceTimer = setTimeout(() => this.#goNext(), seconds * 1000);
  }

  /**
   * Kiosk watchdog: a full reload every night flushes anything that
   * accumulates in a week-long browser session — ours or the browser's.
   * Jittered so the screens don't all hit the server at once.
   */
  #scheduleNightlyReload() {
    const now = new Date();
    const next = new Date(now);
    next.setHours(RELOAD_HOUR, 0, 0, 0);
    if (next <= now) next.setDate(next.getDate() + 1);
    const jitter = Math.floor(Math.random() * 5 * 60 * 1000);
    this.#reloadTimer = setTimeout(
      () => window.location.reload(),
      next - now + jitter
    );
  }
}

customElements.define("wdka-carousel", WdkaCarousel);
